import React, { useState, useEffect } from 'react';
import { Box, Typography, Container, Card, CardContent, CardMedia, Button, Grid, Paper, Divider } from '@mui/material';
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled';
import CodeIcon from '@mui/icons-material/Code';
import useRoleSelection from '../hooks/useRoleSelection';
import AdvertiserPlayground from '../components/playground/AdvertiserPlayground';
import PublisherPlayground from '../components/playground/PublisherPlayground';

const Playground: React.FC = () => {
  const { role, setRole, resetPlayground } = useRoleSelection();

  return (
    <Box sx={{ padding: 4, backgroundColor: '#0F172A', minHeight: 'calc(100vh - 64px)' }}>
      {!role ? (
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h3" component="h1" sx={{ fontWeight: '700', mb: 2, color: 'white' }}>
              Reasonic Interactive Playground
            </Typography>
            <Typography variant="h6" sx={{ color: '#94A3B8', mb: 4 }}>
              Try out our platform features without signing up. Choose your role to get started.
            </Typography>
          </Box>

          <Grid container spacing={4} justifyContent="center">
            <Grid item xs={12} md={5}>
              <Card 
                elevation={8}
                sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  backgroundColor: '#1E293B',
                  borderRadius: 2,
                  transition: 'transform 0.3s, box-shadow 0.3s',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 12px 20px rgba(0, 0, 0, 0.3)'
                  }
                }}
              >
                <CardMedia
                  component="div"
                  sx={{
                    height: 140,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(59, 130, 246, 0.1)'
                  }}
                >
                  <PlayCircleFilledIcon sx={{ fontSize: 80, color: '#3B82F6' }} />
                </CardMedia>
                <CardContent sx={{ flexGrow: 1, p: 3 }}>
                  <Typography gutterBottom variant="h5" component="h2" sx={{ fontWeight: 600, color: 'white' }}>
                    Advertiser Experience
                  </Typography>
                  <Typography sx={{ mb: 3, color: '#94A3B8' }}>
                    Generate AI-powered ad content, explore campaign strategies, and view simulated performance metrics.
                  </Typography>
                  <Box sx={{ mt: 2 }}>
                    <Typography component="div" variant="body2" sx={{ color: '#CBD5E1', mb: 1 }}>
                      • Create ad campaigns with LLM assistance
                    </Typography>
                    <Typography component="div" variant="body2" sx={{ color: '#CBD5E1', mb: 1 }}>
                      • Customize persona and tone
                    </Typography>
                    <Typography component="div" variant="body2" sx={{ color: '#CBD5E1', mb: 1 }}>
                      • Preview performance analytics
                    </Typography>
                  </Box>
                  <Box sx={{ mt: 4 }}>
                    <Button 
                      onClick={() => setRole('advertiser')}
                      variant="contained" 
                      fullWidth
                      size="large"
                      sx={{ 
                        backgroundColor: '#3B82F6', 
                        '&:hover': { backgroundColor: '#2563EB' },
                        py: 1
                      }}
                    >
                      Enter as Advertiser
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={5}>
              <Card 
                elevation={8}
                sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  backgroundColor: '#1E293B',
                  borderRadius: 2,
                  transition: 'transform 0.3s, box-shadow 0.3s',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 12px 20px rgba(0, 0, 0, 0.3)'
                  }
                }}
              >
                <CardMedia
                  component="div"
                  sx={{
                    height: 140,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(16, 185, 129, 0.1)'
                  }}
                >
                  <CodeIcon sx={{ fontSize: 80, color: '#10B981' }} />
                </CardMedia>
                <CardContent sx={{ flexGrow: 1, p: 3 }}>
                  <Typography gutterBottom variant="h5" component="h2" sx={{ fontWeight: 600, color: 'white' }}>
                    Publisher Experience
                  </Typography>
                  <Typography sx={{ mb: 3, color: '#94A3B8' }}>
                    Test SDK integration, customize ad placement options, and visualize revenue potential.
                  </Typography>
                  <Box sx={{ mt: 2 }}>
                    <Typography component="div" variant="body2" sx={{ color: '#CBD5E1', mb: 1 }}>
                      • Generate integration code snippets
                    </Typography>
                    <Typography component="div" variant="body2" sx={{ color: '#CBD5E1', mb: 1 }}>
                      • Configure ad placement strategy
                    </Typography>
                    <Typography component="div" variant="body2" sx={{ color: '#CBD5E1', mb: 1 }}>
                      • Preview SDK behavior and metrics
                    </Typography>
                  </Box>
                  <Box sx={{ mt: 4 }}>
                    <Button 
                      onClick={() => setRole('publisher')}
                      variant="contained" 
                      fullWidth
                      size="large"
                      sx={{ 
                        backgroundColor: '#10B981', 
                        '&:hover': { backgroundColor: '#059669' },
                        py: 1
                      }}
                    >
                      Enter as Publisher
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Box sx={{ mt: 8, textAlign: 'center' }}>
            <Typography variant="body1" sx={{ color: '#94A3B8', mb: 2 }}>
              Want to try the full experience?
            </Typography>
            <Button 
              variant="outlined" 
              href="/signup"
              sx={{ 
                borderColor: '#3B82F6', 
                color: '#3B82F6',
                '&:hover': { 
                  borderColor: '#2563EB', 
                  backgroundColor: 'rgba(59, 130, 246, 0.05)' 
                },
                px: 4
              }}
            >
              Sign Up for Free
            </Button>
          </Box>
        </Container>
      ) : role === 'advertiser' ? (
        <AdvertiserPlayground onRoleSwitch={() => setRole('publisher')} onReset={resetPlayground} />
      ) : (
        <PublisherPlayground onRoleSwitch={() => setRole('advertiser')} onReset={resetPlayground} />
      )}
    </Box>
  );
};

export default Playground; 